import { ReactNode } from "react";
import { Link } from "react-router-dom";

interface CTAPanelProps {
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    href: string;
  };
  secondaryAction?: {
    label: string;
    href: string;
  };
  children?: ReactNode;
  variant?: "light" | "dark";
  ctaText?: string;
  ctaLink?: string;
  ctaExternal?: boolean;
}

function isInternal(href: string) {
  return href.startsWith("/");
}

export default function CTAPanel({
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  variant = "dark",
  ctaText,
  ctaLink,
}: CTAPanelProps) {
  const isDark = variant === "dark";

  const actionButtonStyle = isDark
    ? "btn-secondary border-white/30 text-white hover:bg-white/10"
    : "btn-secondary";

  const renderAction = (action: { label: string; href: string }, style: string) => {
    const inner = (
      <>
        {action.label}
        <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
      </>
    );
    return isInternal(action.href) ? (
      <Link to={action.href} className={style} aria-label={action.label}>
        {inner}
      </Link>
    ) : (
      <a
        href={action.href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className={style}
        aria-label={action.label}
      >
        {inner}
      </a>
    );
  };

  const renderActionButtons = () => (
    <>
      {primaryAction && renderAction(primaryAction, "btn-primary")}
      {secondaryAction && renderAction(secondaryAction, actionButtonStyle)}
    </>
  );

  return (
    <section className={isDark ? "bg-primary-800" : "bg-background-100"}>
      <div className="container-page py-16 md:py-20 text-center">
        <h2 className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-4 ${isDark ? "text-white" : "text-foreground-950"}`}>
          {title}
        </h2>
        <p className={`text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed ${isDark ? "text-foreground-300" : "text-foreground-600"}`}>
          {description}
        </p>
        {children}
        {(primaryAction || secondaryAction || (ctaText && ctaLink)) && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            {ctaText && ctaLink ? (
              <a
                href={ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                aria-label={`${ctaText} — Mitaja Corporation Capability Statement (PDF)`}
              >
                <i className="ri-download-line mr-2" aria-hidden="true"></i>
                {ctaText}
                <span className="sr-only"> (PDF)</span>
              </a>
            ) : (
              renderActionButtons()
            )}
          </div>
        )}
      </div>
    </section>
  );
}